<template>
  <div
    v-on-clickaway="close"
    :class="classes"
  >
    <div
      class="select__field-wrapper"
      :class="{ disabled: disabled }"
      @click="onActivate"
    >
      <label
        v-if="label"
        :for="id"
        class="select__label"
      >
        {{ label }}
        <span v-if="required" class="select__label-required">*</span>
      </label>

      <div class="select__field">
        <div class="select__text">
          <span v-if="text !== placeholder">{{ text }}</span>
        </div>
        <ChevronIcon
          v-if="!hideArrow && items.length > 1 || !autoSelect"
          class="select__arrow"
        />
      </div>
    </div>

    <div
      v-if="items.length > 1 || !autoSelect"
      :class="['select__dropdown', `select__dropdown_scrollbar_${scrollbar}`]"
    >
      <div
        v-for="(item, index) in filteredItems"
        :key="`select-item-${index}-in-${id}`"
        class="select__dropdown-item"
        @click="onItemSelect(item)"
      >
        {{ item[itemTextKey] }}
        <span>{{ item['second'] }}</span>
      </div>
    </div>

    <!-- <transition name="slide-from-top"> -->
    <span
      v-if="error && errorMsg"
      class="select__error-msg"
    >
      {{ errorMsg }}
    </span>
    <ErrorIcon
      v-if="error && errorMsg"
      class="select__error-msg-icon"
    />
    <!-- </transition> -->
  </div>
</template>

<script>
  import ChevronIcon from '@/assets/img/icons/chevron.svg?inline'
  import { mixin as clickaway } from 'vue-clickaway'
  import { get } from 'lodash'
  import ErrorIcon from '@/assets/img/icons/error.svg?inline'

  export default {
    name: 'BaseSelect',
    components: {
      ChevronIcon,
      ErrorIcon
    },
    mixins: [ clickaway ],
    props: {
      emitOnCreate: {
        type: Boolean,
        default: true
      },

      itsDouble: {
        type: Boolean,
        default: false
      },

      value: {
        type: null,
        default: ''
      },
      autoSelect: {
        type: Boolean,
        default: false
      },
      disabled: {
        type: [Boolean, Number, String],
        default: false
      },
      required: {
        type: [Boolean, String],
        default: false
      },
      label: {
        type: String,
        default: ''
      },
      placeholder: {
        type: String,
        default: ''
      },
      items: {
        type: Array,
        requied: true
      },
      itemValueKey: {
        type: String,
        default: 'value'
      },
      itemTextKey: {
        type: String,
        default: 'text'
      },
      errorMsg: {
        type: String,
        default: ''
      },
      scrollbar: {
        type: String,
        default: 'custom'
      },
      disable: {
        type: Boolean,
        default: false
      },
      hideArrow: {
        type: Boolean,
        default: false
      }
    },
    data () {
      return {
        isActive: false,
        error: null
      }
    },
    computed: {
      selected () {
        return !this.value ? null : this.items.filter(({ value }) => value === this.value)[0]
      },
      id () {
        return `select-${this._uid}`
      },
      text () {
        return get(this, ['selected', this.itemTextKey]) || this.placeholder
      },
      classes () {
        return [
          'select',
          {
            'select_active': this.isActive,
            'select_error': this.error,
            'select_no-items': this.items.length < 1 || this.disable,
            'select_has-value': this.text !== this.placeholder
          }
        ]
      },
      filteredItems () {
        const filteredItems = this.items.filter(item => item.value !== get(this, 'selected.value'))
        if (this.items.length === 1 && get(this, 'selected.value')) {
          filteredItems.push({ text: this.placeholder })
        }
        return filteredItems
      }
    },
    watch: {
      'items': {
        handler (items) {
          if (items.length === 1 && this.autoSelect) {
            this.onItemSelect(items[0])
          }
        },
        immediate: true
      },
      errorMsg: {
        handler (msg) {
          this.error = !!msg
        },
        immediate: true
      }
    },
    mounted () {
      if (this.items.length === 1 && this.autoSelect) {
        this.onItemSelect(this.items[0])
      }
    },
    created () {
      if (!this.placeholder && this.emitOnCreate) {
        this.onItemSelect(this.items[0])
      }
    },
    methods: {
      onActivate () {
        this.isActive = !this.isActive
        this.error = false
      },
      onItemSelect (item) {
        const selected = item || {}
        this.$emit('input', selected[this.itemValueKey], selected)
        this.close()
      },
      close () {
        this.isActive = false
      }
    }
  }
</script>

<style lang="scss">
  $padding-for-error: 30px;
  $active-box-shadow: 2px 2px 5px rgba($c-black, 0.1);

  .select {
    position: relative;
    display: block;
    min-width: 290px;
    // max-width: 335px;
    margin-bottom: 16px !important;
    cursor: pointer;

    @include mobile {
      max-width: 100%;
      margin-bottom: 5px !important;
    }

    &.select_has-value{
      .select__label{
        top: 6px;
        font-size: 10px;
        line-height: 20px;
      }
    }
    &.select_active{
      .select__text span{
        white-space: unset;
        max-width: unset;
      }
    }
    &__label {
      position: absolute;
      z-index: 1;
      top: 16px;
      left: 20px;
      display: inline-block;
      padding-bottom: 1em;
      font-family: NobelWGL-Book;
      font-weight: 400;
      font-style: Book;
      font-size: 16px;
      line-height: 30px;
      letter-spacing: 1px;
      vertical-align: middle;

      color: $c-gray-new;
      pointer-events: none;
      transition: .35;

      &-required {
        color: $c-error;
      }
    }

    &__text {
      display: grid;
      position: relative;
      padding-right: 10px;
      color: $c-gray-dark;
      font-family: NobelWGL-Book;
      font-weight: 400;
      font-size: 18px;
      letter-spacing: 1px;
      span{
        max-width: 100%;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        display: block;

      }
    }

    &__field-wrapper {
      &.disabled {
        pointer-events: none;
        .select__field{
          background: #EEEDEC!important;
        }
        .select__text span{
          color: #6C6F76!important;
        }
        .select__label{
          color: #6C6F76!important;
        }
      }
    }

    &__field {
      position: relative;
      display: block;
      width: 100%;
      min-height: 56px;
      padding: 24px 20px 10px;
      font-size: 15px;
      line-height: 1;
      border: 1px solid $c-gray-light;
      outline: none;
      caret-color: $c-active-light;
      transition: 0.3s;
      border-radius: 0;

      @include hover {
        background-color: $gray;
      }
    }

    &__arrow {
      position: absolute;
      right: 4%;
      bottom: 35%;
      width: 13px;
      height: 13px;
      stroke: $c-black-mind;
      transition: 0.3s;
      transform: rotate(-90deg);
    }

    &__dropdown {
      position: absolute;
      top: calc(100% - #{$padding-for-error});
      left: 0;
      z-index: 999;
      width: 100%;
      max-height: 270px;
      overflow-y: auto;
      visibility: hidden;
      border-bottom-right-radius: 4px;
      border-bottom-left-radius: 4px;
      box-shadow: $active-box-shadow;
      opacity: 0;
      transition: 0.3s;
      -webkit-overflow-scrolling: touch;

      &_scrollbar {
        &_custom {
          @include custom-scrollbar();
        }

        &_none {
          @include no-scrollbar();
        }
      }

      &-item {
        padding: 0.938em 1.3em;
        font-size: 15px;
        line-height: 1;
        color: #333;
        background-color: $c-white;
        border-top: 1px solid rgba(0, 0, 0, $alpha: 0.05);
        transition: background-color 0.3s;

        display: flex;
        justify-content: space-between;

        span {
          font-size: 14px;
          color: #6a6b70;
        }

        @include hover {
          background-color: $gray;
        }
      }
    }

    &__error-msg {
      position: absolute;
      bottom: 14px;
      left: 0;
      z-index: 1;
      height: 0;
      font-size: 12px;
      color: $c-error;

      &-icon {
        position: absolute;
        top: 91px;
        right: 0;
        fill: $c-error;
      }
    }

    &_error {
      padding-bottom: 20px;

      .select {
        &__field {
          border: 1px solid $c-error;
        }
      }
    }

    &_active {
      .select {
        &__field {
          border-bottom-right-radius: 0;
          border-bottom-left-radius: 0;
          box-shadow: $active-box-shadow;
        }

        &__arrow {
          transform: rotate(-270deg);
        }

        &__dropdown {
          //padding-top: 36px;
          margin-top: 30px;
          visibility: visible;
          opacity: 1;
        }
      }
    }
  }
</style>
